import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  IconButton,
  Chip,
  Tooltip,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Delete, Edit, Launch, Add, Visibility } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query'; //-tech-debt-react-query;
import { CacheKey } from '../../../types/cache-keys';
import {
  useAddItemMutation,
  useFetchItems,
  useUpdateItemMutation,
  useDeleteItemMutation,
} from '../../../hooks/base-hooks';
import ResourceEditorDialog from '../Editors/ResourceEditorDialog';
import { VersionScope } from '../../../types/snackableCourse';

type Props = {
  joinEndpoint: string;
  joinCacheKey: CacheKey;
  parentField: string;
  parentId: number | null;

  title?: string;
  showNameColumn?: boolean;
  maxHeight?: number;

  invalidateKeys?: CacheKey[];

  disabled?: boolean;
  readOnly?: boolean;

  canCreate?: boolean;
  canUpdate?: boolean;
  canDelete?: boolean;

  /**
   * Optional version scope.
   * If product_version_id/doc_version/deployment_scope is provided,
   * resource rows are filtered to that version scope and newly created relation rows inherit it.
   *
   * Scope is stored on the join/relation row, not on the resource row.
   */
  versionScope?: VersionScope | null;
  showVersionScopeColumn?: boolean;
};

const hasMeaningfulVersionScope = (versionScope?: VersionScope | null) => {
  if (!versionScope) return false;

  return Boolean(
    versionScope.product_id ||
    versionScope.product_version_id ||
    versionScope.product_license_id ||
    versionScope.doc_version ||
    versionScope.deployment_scope ||
    versionScope.generation_scope ||
    versionScope.version_source
  );
};

const toNumberOrNull = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;

  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  if (typeof value === 'object' && value !== null && 'id' in value) {
    return toNumberOrNull((value as any).id);
  }

  return null;
};

const normalized = (value: unknown): string => {
  return String(value ?? '').trim().toLowerCase();
};

const getResourceFromJoinRow = (joinRow: any, resourceById: Map<number, any>) => {
  if (joinRow?.resource && typeof joinRow.resource === 'object') {
    return joinRow.resource;
  }

  const resourceId =
    toNumberOrNull(joinRow?.resource) ??
    toNumberOrNull(joinRow?.resource_id);

  if (resourceId == null) return null;

  return resourceById.get(resourceId) ?? null;
};

const getJoinProductId = (joinRow: any): number | null => {
  return (
    toNumberOrNull(joinRow?.product_id) ??
    toNumberOrNull(joinRow?.product) ??
    null
  );
};

const getJoinVersionId = (joinRow: any): number | null => {
  return (
    toNumberOrNull(joinRow?.product_version_id) ??
    toNumberOrNull(joinRow?.product_version) ??
    null
  );
};

const getJoinLicenseId = (joinRow: any): number | null => {
  return (
    toNumberOrNull(joinRow?.product_license_id) ??
    toNumberOrNull(joinRow?.product_license) ??
    null
  );
};

const getLegacyResourceProductId = (resource: any): number | null => {
  return (
    toNumberOrNull(resource?.product_id) ??
    toNumberOrNull(resource?.product) ??
    null
  );
};

const getLegacyResourceVersionId = (resource: any): number | null => {
  return (
    toNumberOrNull(resource?.product_version_id) ??
    toNumberOrNull(resource?.product_version) ??
    null
  );
};

const getLegacyResourceLicenseId = (resource: any): number | null => {
  return (
    toNumberOrNull(resource?.product_license_id) ??
    toNumberOrNull(resource?.product_license) ??
    null
  );
};

const getScopedProductId = (joinRow: any, resource: any): number | null => {
  return getJoinProductId(joinRow) ?? getLegacyResourceProductId(resource);
};

const getScopedVersionId = (joinRow: any, resource: any): number | null => {
  return getJoinVersionId(joinRow) ?? getLegacyResourceVersionId(resource);
};

const getScopedLicenseId = (joinRow: any, resource: any): number | null => {
  return getJoinLicenseId(joinRow) ?? getLegacyResourceLicenseId(resource);
};

const getScopedDocVersion = (joinRow: any, resource: any): string => {
  return String(joinRow?.doc_version ?? resource?.doc_version ?? '').trim();
};

const getScopedDeploymentScope = (joinRow: any, resource: any): string => {
  return String(joinRow?.deployment_scope ?? resource?.deployment_scope ?? '').trim();
};

const getScopedGenerationScope = (joinRow: any, resource: any): string => {
  return String(joinRow?.generation_scope ?? resource?.generation_scope ?? '').trim();
};

const getScopedVersionSource = (joinRow: any, resource: any): string => {
  return String(joinRow?.version_source ?? resource?.version_source ?? '').trim();
};

const getScopedVersionName = (joinRow: any, resource: any): string => {
  if (joinRow?.product_version_name) return String(joinRow.product_version_name).trim();
  if (joinRow?.version_name) return String(joinRow.version_name).trim();

  if (joinRow?.product_version && typeof joinRow.product_version === 'object') {
    return String(
      joinRow.product_version.version ??
      joinRow.product_version.name ??
      ''
    ).trim();
  }

  if (resource?.product_version_name) return String(resource.product_version_name).trim();
  if (resource?.version_name) return String(resource.version_name).trim();

  if (resource?.product_version && typeof resource.product_version === 'object') {
    return String(
      resource.product_version.version ??
      resource.product_version.name ??
      ''
    ).trim();
  }

  return '';
};

const getScopedLicenseName = (joinRow: any, resource: any): string => {
  if (joinRow?.product_license_name) return String(joinRow.product_license_name).trim();
  if (joinRow?.license_name) return String(joinRow.license_name).trim();

  if (joinRow?.product_license && typeof joinRow.product_license === 'object') {
    return String(
      joinRow.product_license.name ??
      ''
    ).trim();
  }

  if (resource?.product_license_name) return String(resource.product_license_name).trim();
  if (resource?.license_name) return String(resource.license_name).trim();

  if (resource?.product_license && typeof resource.product_license === 'object') {
    return String(
      resource.product_license.name ??
      ''
    ).trim();
  }

  return '';
};

const resourceJoinMatchesVersionScope = (
  joinRow: any,
  resource: any,
  versionScope?: VersionScope | null
): boolean => {
  if (!hasMeaningfulVersionScope(versionScope)) return true;
  if (!joinRow && !resource) return false;

  if (versionScope?.product_id != null) {
    const scopedProductId = getScopedProductId(joinRow, resource);

    if (
      scopedProductId != null &&
      Number(scopedProductId) !== Number(versionScope.product_id)
    ) {
      return false;
    }
  }

  if (versionScope?.product_version_id != null) {
    return Number(getScopedVersionId(joinRow, resource)) === Number(versionScope.product_version_id);
  }

  if (versionScope?.doc_version) {
    return normalized(getScopedDocVersion(joinRow, resource)) === normalized(versionScope.doc_version);
  }

  if (versionScope?.deployment_scope) {
    return normalized(getScopedDeploymentScope(joinRow, resource)) === normalized(versionScope.deployment_scope);
  }

  if (versionScope?.product_license_id != null) {
    const scopedLicenseId = getScopedLicenseId(joinRow, resource);
    return scopedLicenseId != null && Number(scopedLicenseId) === Number(versionScope.product_license_id);
  }

  return true;
};

const buildVersionScopeLabel = (joinRow: any, resource: any) => {
  const docVersion = getScopedDocVersion(joinRow, resource);
  const versionName = getScopedVersionName(joinRow, resource);
  const versionId = getScopedVersionId(joinRow, resource);
  const deploymentScope = getScopedDeploymentScope(joinRow, resource);

  const normalizedDeployment = normalized(deploymentScope);
  const normalizedDocVersion = normalized(docVersion || versionName);

  if (normalizedDeployment === 'saas' || normalizedDocVersion === 'saas') {
    return 'SaaS';
  }

  const versionLabel =
    docVersion ||
    versionName ||
    (versionId ? String(versionId) : '');

  const parts = [
    versionLabel ? `Version: ${versionLabel}` : '',
    deploymentScope ? `Deployment: ${deploymentScope}` : '',
  ].filter(Boolean);

  return parts.length ? parts.join(' • ') : 'Generic';
};

const buildJoinVersionScopePayload = ({
                                        versionScope,
                                        parentField,
                                        parentId,
                                      }: {
  versionScope?: VersionScope | null;
  parentField: string;
  parentId: number | null;
}) => {
  if (!hasMeaningfulVersionScope(versionScope)) return {};

  const payload: Record<string, any> = {
    doc_version: versionScope?.doc_version || null,
    deployment_scope: versionScope?.deployment_scope || null,
    generation_scope: versionScope?.generation_scope || null,
    version_source: versionScope?.version_source || null,
  };

  if (versionScope?.product_id != null) {
    if (parentField === 'product') {
      payload.product = parentId ?? versionScope.product_id;
    } else {
      payload.product = versionScope.product_id;
    }
  }

  if (versionScope?.product_version_id != null) {
    payload.product_version = versionScope.product_version_id;
  }

  if (versionScope?.product_license_id != null) {
    payload.product_license = versionScope.product_license_id;
  }

  return payload;
};

const dispatchResourceRelationsChanged = ({
                                            entityType,
                                            entityId,
                                            joinCacheKey,
                                            versionScope,
                                          }: {
  entityType?: string;
  entityId?: number | null;
  joinCacheKey: CacheKey;
  versionScope?: VersionScope | null;
}) => {
  try {
    window.dispatchEvent(
      new CustomEvent('toolbox-resource-relations-changed', {
        detail: {
          entityType,
          entityId,
          joinCacheKey,
          versionScope,
          ts: Date.now(),
        },
      })
    );
  } catch {
    // no-op
  }
};

const parentFieldToEntityType = (parentField: string): string => {
  const map: Record<string, string> = {
    product: 'product',
    product_feature: 'product_feature',
    product_component: 'product_component',
    product_use_case: 'product_use_case',
    go_to_market: 'go_to_market',
    solution_component: 'solution_component',
    security_use_case: 'security_use_case',
    security_use_case_component: 'security_use_case_component',
  };

  return map[parentField] ?? parentField;
};

export default function ResourceRelationPanel({
                                                joinEndpoint,
                                                joinCacheKey,
                                                parentField,
                                                parentId,
                                                title = 'Resources',
                                                showNameColumn = true,
                                                maxHeight = 400,
                                                invalidateKeys = [],
                                                disabled = false,
                                                readOnly = false,
                                                canCreate = true,
                                                canUpdate = true,
                                                canDelete = true,
                                                versionScope = null,
                                                showVersionScopeColumn = true,
                                              }: Props) {
  const queryClient = useQueryClient();

  const [editingResource, setEditingResource] = useState<any>(null);
  const [resourceDialogMode, setResourceDialogMode] = useState<'create' | 'edit'>('create');
  const [resourceDialogOpen, setResourceDialogOpen] = useState(false);

  const isDisabled = disabled || !parentId;
  const versionScoped = hasMeaningfulVersionScope(versionScope);

  const effectiveCanCreate = !readOnly && canCreate;
  const effectiveCanUpdate = !readOnly && canUpdate;
  const effectiveCanDelete = !readOnly && canDelete;

  const entityType = parentFieldToEntityType(parentField);

  const resourcesJoinQuery = useFetchItems<any[]>(
    joinEndpoint,
    () => [joinCacheKey, parentField, String(parentId)],
    parentId ? { [parentField]: parentId, detailed: true, size: 1000 } : {},
    { enabled: !!parentId },
  );

  const resourcesQuery = useFetchItems<any[]>(
    'db/resource',
    CacheKey.Resource,
    { size: 1000 },
    { enabled: !!parentId },
  );

  const resourceById = useMemo(() => {
    const map = new Map<number, any>();

    (resourcesQuery.data ?? []).forEach((r: any) => {
      if (r.id) map.set(Number(r.id), r);
    });

    (resourcesJoinQuery.data ?? []).forEach((jr: any) => {
      if (jr.resource && typeof jr.resource === 'object') {
        map.set(Number(jr.resource.id), jr.resource);
      }
    });

    return map;
  }, [resourcesQuery.data, resourcesJoinQuery.data]);

  const addResource = useAddItemMutation('db/resource', () => [CacheKey.Resource]);
  const updateResource = useUpdateItemMutation('db/resource', () => [CacheKey.Resource]);
  const deleteResource = useDeleteItemMutation('db/resource', () => [CacheKey.Resource]);

  const addResourceJoin = useAddItemMutation(
    joinEndpoint,
    () => [joinCacheKey, parentField, String(parentId)],
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: [joinCacheKey] });
        await queryClient.invalidateQueries({ queryKey: [CacheKey.Resource] });
        for (const k of invalidateKeys) {
          await queryClient.invalidateQueries({ queryKey: [k] });
        }

        dispatchResourceRelationsChanged({
          entityType,
          entityId: parentId,
          joinCacheKey,
          versionScope,
        });
      },
    }
  );

  const deleteResourceJoin = useDeleteItemMutation(joinEndpoint, (payload) => [
    joinCacheKey,
    parentField,
    String(parentId),
    String(payload.id),
  ]);

  const joinRows = useMemo(() => {
    const rawRows = resourcesJoinQuery.data ?? [];

    if (!versionScoped) return rawRows;

    return rawRows.filter((joinRow: any) => {
      const resource = getResourceFromJoinRow(joinRow, resourceById);
      return resourceJoinMatchesVersionScope(joinRow, resource, versionScope);
    });
  }, [resourcesJoinQuery.data, resourceById, versionScoped, versionScope]);

  const handleCreate = () => {
    if (isDisabled) return;
    if (!effectiveCanCreate) return;

    setResourceDialogMode('create');
    setEditingResource(null);
    setResourceDialogOpen(true);
  };

  const handleEditOrView = (joinRow: any) => {
    if (isDisabled) return;

    const resource = getResourceFromJoinRow(joinRow, resourceById);
    if (!resource) return;

    setResourceDialogMode('edit');
    setEditingResource({
      ...resource,
      product_id: getScopedProductId(joinRow, resource),
      product_version_id: getScopedVersionId(joinRow, resource),
      product_license_id: getScopedLicenseId(joinRow, resource),
      doc_version: getScopedDocVersion(joinRow, resource),
      deployment_scope: getScopedDeploymentScope(joinRow, resource),
      generation_scope: getScopedGenerationScope(joinRow, resource),
      version_source: getScopedVersionSource(joinRow, resource),
    });
    setResourceDialogOpen(true);
  };

  const handleDelete = async (joinRow: any) => {
    if (isDisabled) return;
    if (!effectiveCanDelete) return;

    const resource = getResourceFromJoinRow(joinRow, resourceById);
    const resourceId = resource?.id || toNumberOrNull(joinRow.resource);
    const joinId = joinRow.id;

    if (!resourceId || !joinId) {
      console.error('Missing resource or join ID:', { resourceId, joinId, joinRow });
      return;
    }

    const confirmed = window.confirm(
      `This will permanently delete the resource "${resource?.name || 'Untitled'}" and remove it from this item. Continue?`,
    );
    if (!confirmed) return;

    try {
      await deleteResourceJoin.mutateAsync({ id: joinId });
      await deleteResource.mutateAsync({ id: resourceId });

      await queryClient.invalidateQueries({ queryKey: [CacheKey.Resource] });
      await queryClient.invalidateQueries({ queryKey: [joinCacheKey] });
      for (const k of invalidateKeys) {
        await queryClient.invalidateQueries({ queryKey: [k] });
      }

      dispatchResourceRelationsChanged({
        entityType,
        entityId: parentId,
        joinCacheKey,
        versionScope,
      });
    } catch (error) {
      console.error('Failed to delete resource:', error);
    }
  };

  const handleResourceSaved = async (savedResource?: any) => {
    if (readOnly) {
      setResourceDialogOpen(false);
      setEditingResource(null);
      return;
    }

    if (resourceDialogMode === 'create' && savedResource?.id && parentId) {
      try {
        await addResourceJoin.mutateAsync({
          [parentField]: parentId,
          resource: savedResource.id,
          ...buildJoinVersionScopePayload({
            versionScope,
            parentField,
            parentId,
          }),
        });
      } catch (error) {
        console.error('Failed to attach resource:', error);
      }
    } else {
      dispatchResourceRelationsChanged({
        entityType,
        entityId: parentId,
        joinCacheKey,
        versionScope,
      });
    }

    await queryClient.invalidateQueries({ queryKey: [CacheKey.Resource] });
    await queryClient.invalidateQueries({ queryKey: [joinCacheKey] });
    for (const k of invalidateKeys) {
      await queryClient.invalidateQueries({ queryKey: [k] });
    }

    setResourceDialogOpen(false);
    setEditingResource(null);
  };

  const loading =
    resourcesJoinQuery.isLoading ||
    resourcesQuery.isLoading ||
    addResourceJoin.isPending ||
    deleteResourceJoin.isPending ||
    addResource.isPending ||
    updateResource.isPending ||
    deleteResource.isPending;

  const effectiveTitle = versionScoped
    ? `${title} for Selected Version`
    : title;

  const columnCount =
    (showNameColumn ? 1 : 0) +
    1 +
    1 +
    (showVersionScopeColumn ? 1 : 0) +
    1;

  return (
    <>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              {effectiveTitle}
            </Typography>
            {versionScoped && (
              <Typography variant="caption" color="text.secondary">
                Showing only resources scoped to the selected version/deployment context.
              </Typography>
            )}
          </Box>

          <Box display="flex" gap={1} alignItems="center">
            {loading && <CircularProgress size={18} />}

            <Button
              startIcon={<Add />}
              size="small"
              variant="outlined"
              onClick={handleCreate}
              disabled={isDisabled || !effectiveCanCreate}
            >
              New Resource
            </Button>
          </Box>
        </Box>

        {versionScoped && (
          <Alert severity="info" sx={{ mb: 2 }}>
            New resources created here will inherit the selected product/version scope.
          </Alert>
        )}

        {isDisabled && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Save the parent record first to manage resources.
          </Typography>
        )}

        {!isDisabled && (
          <TableContainer sx={{ maxHeight }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {showNameColumn && <TableCell>Name</TableCell>}
                  <TableCell>Type</TableCell>
                  <TableCell>Provider</TableCell>
                  {showVersionScopeColumn && <TableCell>Scope</TableCell>}
                  <TableCell width={140}>Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {joinRows.map((jr: any) => {
                  const res = getResourceFromJoinRow(jr, resourceById);
                  if (!res) return null;

                  return (
                    <TableRow key={jr.id}>
                      {showNameColumn && (
                        <TableCell>
                          <Box>
                            <Typography variant="body2">{res.name || 'Untitled'}</Typography>
                            {res.description && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  display: 'block',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  maxWidth: 300,
                                }}
                              >
                                {res.description.substring(0, 100)}
                                {res.description.length > 100 ? '...' : ''}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                      )}

                      <TableCell>{res.type ? <Chip label={res.type} size="small" /> : '-'}</TableCell>

                      <TableCell>
                        {res.provider ? <Chip label={res.provider} size="small" variant="outlined" /> : '-'}
                      </TableCell>

                      {showVersionScopeColumn && (
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {buildVersionScopeLabel(jr, res)}
                          </Typography>
                        </TableCell>
                      )}

                      <TableCell>
                        <Box display="flex" gap={0.5}>
                          {res.url && (
                            <Tooltip title="Open URL">
                              <IconButton size="small" href={res.url} target="_blank" rel="noopener noreferrer">
                                <Launch fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}

                          {effectiveCanUpdate ? (
                            <Tooltip title="Edit Resource">
                              <IconButton size="small" onClick={() => handleEditOrView(jr)}>
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="View Resource">
                              <IconButton size="small" onClick={() => handleEditOrView(jr)}>
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}

                          {effectiveCanDelete && (
                            <Tooltip title="Delete Resource">
                              <IconButton size="small" color="error" onClick={() => handleDelete(jr)}>
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}

                {joinRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={columnCount}>
                      <Typography variant="body2" color="text.secondary">
                        No resources yet{effectiveCanCreate ? '. Click "New Resource" to create one.' : '.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <ResourceEditorDialog
        open={resourceDialogOpen}
        mode={resourceDialogMode}
        resource={editingResource}
        versionScope={versionScope}
        onClose={() => {
          setResourceDialogOpen(false);
          setEditingResource(null);
        }}
        onSaved={handleResourceSaved}
        readOnly={readOnly || !canUpdate}
      />
    </>
  );
}